<template>
  <!-- "Read my chart": every angle with its meaning, in the Mystic's own voice.
       Between us first — the seeker has just read "within me" on the screen
       above. The same sheet from the biography's fold and the My Mystic header. -->
  <div class="sheet">
    <section class="sheet-group">
      <h3 class="sheet-label">Between us</h3>
      <template v-if="(chart.contacts || []).length">
        <p class="sheet-sub">My bodies against yours. These are why I was made the way I was made.</p>
        <p v-if="chart.bond" class="sheet-bond">{{ chart.bond }}</p>
        <ul class="sheet-lines">
          <li v-for="(c, i) in chart.contacts" :key="'c' + i" class="sheet-line">
            <span class="line-meaning">{{ meaning(c, true) }}</span>
            <span class="line-tech">my <em>{{ cap(c.from) }}</em>
              <span class="verb" :title="hint(c.aspect)">{{ verb(c.aspect) }}</span>
              your <em>{{ cap(c.to) }}</em>
              <span class="orb">{{ c.orb }}°</span></span>
          </li>
        </ul>
      </template>
      <p v-else class="sheet-sub">I was cast on my own — there was no chart of yours to set mine against. Add your birth moment to your Mirror and the next Mystic you make will be drawn against it.</p>
    </section>

    <section class="sheet-group">
      <h3 class="sheet-label">Within me</h3>
      <p class="sheet-sub">Angles between my own bodies, tightest first. A smaller degree is a tighter angle, and tighter is stronger.</p>
      <ul class="sheet-lines">
        <li v-for="(a, i) in chart.aspects || []" :key="'a' + i" class="sheet-line">
          <span class="line-meaning">{{ meaning(a) }}</span>
          <span class="line-tech">my <em>{{ cap(a.from) }}</em>
            <span class="verb" :title="hint(a.aspect)">{{ verb(a.aspect) }}</span>
            my <em>{{ cap(a.to) }}</em>
            <span class="orb">{{ a.orb }}°</span></span>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup>
import { aspectVerb, capitalise, meaningLine } from '@/utils/mystic-draft'

const props = defineProps({ chart: { type: Object, required: true } })

// What each angle word means, for the curious: a hover, not a key. The key
// was cryptic on its own; the meaning line above every row already does its
// job in plain words.
const HINT = {
  conjunction: '0° — fused, two things acting as one',
  sextile: '60° — an opening, if it is used',
  square: '90° — friction, something to work at',
  trine: '120° — effortless, natural',
  opposition: '180° — a pull in two directions',
}
function hint (aspect) { return HINT[aspect] || '' }

const meaning = meaningLine
const verb = aspectVerb
const cap = capitalise
</script>

<style scoped>
.sheet { display: flex; flex-direction: column; gap: 22px; color: rgba(195, 169, 255, 0.72); font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16px; line-height: 1.5; }
.sheet-label { font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 2px; text-transform: uppercase; color: #c9a94e; margin: 0 0 4px; font-weight: 600; }
.sheet-sub { margin: 0 0 6px; font-size: 13px; line-height: 1.45; color: rgba(195, 169, 255, 0.42); }
.sheet-bond { margin: 4px 0 8px; font-size: 16px; line-height: 1.45; font-style: italic; color: rgba(195, 169, 255, 0.85); }
.sheet-lines { list-style: none; margin: 0; padding: 0; }
.sheet-line { display: flex; flex-direction: column; gap: 2px; padding: 9px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.06); }
.line-meaning { font-size: 15px; line-height: 1.4; color: rgba(195, 169, 255, 0.85); }
.line-tech { font-size: 12px; color: rgba(195, 169, 255, 0.42); }
.line-tech em { font-style: normal; color: rgba(255, 223, 163, 0.75); }
.line-tech .verb { cursor: help; border-bottom: 1px dotted rgba(195, 169, 255, 0.3); }
.orb { margin-left: 6px; color: rgba(195, 169, 255, 0.3); font-variant-numeric: tabular-nums; }
</style>
